import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { Dropdown, Menu, PageHeader, Select, Tabs } from "antd";
import { isDefined } from "@clipboard-health/util-ts";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FACILITY_ROLES } from "src/constants/facility";
import { HCF_USER_EVENTS } from "src/constants/analyticsEvents";
import {
  PROCESS_MODAL_STATE,
  ROLE_MAPPINGS,
  TAB_STATE,
  TEAM_MEMBERS_ADD_READINESS_TEST_ID,
  TEAM_MEMBERS_ADD_READY_ATTRIBUTE,
} from "src/containers/teamMembers/constants";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { EmptyPanel } from "src/containers/teamMembers/EmptyPanel";
import { generateLogData } from "src/containers/teamMembers/logs";
import {
  BulkActionsContainer,
  ColumnHeaderWrapper,
  FilterDropdownContent,
  FilterIcon,
  InfoIcon,
  SortableHeader,
  Tooltip,
} from "src/containers/teamMembers/styles";
import { getHighestAccessRole } from "src/containers/teamMembers/utils";
import { TeamMemberRow } from "src/containers/teamMembers/TeamMemberRow";
import { TeamMemberStatusModal } from "src/containers/teamMembers/TeamMemberStatusModal";
import { WorkplaceUserAdminActionsProvider } from "src/containers/accountManagement/useWorkplaceUserAdminActions";
import { useTeamMembersApiHook } from "src/containers/teamMembers/useTeamMembersAPIHook";
import { type TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import {
  Column,
  LoadingContainer,
  LoadingIcon,
  SearchField,
  WorkerRow,
  WorkerTabs,
  Wrapper,
} from "src/containers/workers/ListWorkers/ListWorkers.styles";
import { updateSearchParams } from "src/containers/workers/ListWorkers/utils";
import { Button } from "src/designsystem/Button/Button";
import { useDebounce } from "src/hooks/useDebounce";
import { useSearchParams } from "src/hooks/useSearchParams";
import { SessionType } from "src/modules/interface";
import { logEvent } from "@src/appV2/lib/analytics";
import { NotificationPreferencesDialogContainer } from "src/containers/teamMembers/NotificationPreferencesDialogContainer";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { InfoPopover } from "src/designsystem/Popover/Popover";
import { useLocalStorage } from "react-use";
import { GETTING_STARTED } from "@src/appV2/redesign/GettingStarted/constants";
import { RestrictedSignedProfile } from "src/components/RestrictedSignedProfile/RestrictedSignedProfile";
import { TeamMemberUnlinkModal } from "./TeamMemberUnlinkModal";
import { EditTeamMemberDialog } from "./EditTeamMemberDialog";
import { type EditTeamMemberSchema } from "./editTeamMemberSchema";
import { useSupportArticles } from "@src/appV2/HelpCenter/api/useSupportArticles";
import { findSupportArticleUrlById } from "@src/appV2/HelpCenter/utils";

const INFO_POPOVER_ADD_TEAM_MEMBER = "infoPopoverAddTeamMember";

type SortByValue = "name" | "role" | "jobTitle";

interface SortOption {
  value: SortByValue;
  label: string;
}

interface AddMemberReadinessState {
  facilityId: string;
  isReady: boolean;
}

const SORT_OPTIONS: SortOption[] = [
  { value: "name", label: "Name" },
  { value: "role", label: "Role" },
  { value: "jobTitle", label: "Job Title" },
];

interface RoleFilterOption {
  id: string;
  label: string;
}

const ROLE_FILTER_OPTIONS: RoleFilterOption[] = Object.values(ROLE_MAPPINGS).map(
  ({ id, label }) => ({ id, label })
);

export function TeamMembers(props: {
  inModal?: boolean;
  hideTabs?: boolean;
  hideAddMemberButton?: boolean;
  addMemberSignal?: number;
  isAddMemberDialogOpen?: boolean;
  onAddMemberDialogOpenChange?: (isOpen: boolean) => void;
  addMemberDraft?: Partial<EditTeamMemberSchema>;
  onAddMemberDraftChange?: (draft: Partial<EditTeamMemberSchema>) => void;
  controlledActiveTab?: TAB_STATE;
  onControlledTabChange?: (tab: TAB_STATE) => void;
  onAddMemberActionDisabledChange?: (isDisabled: boolean) => void;
  facilityId?: string;
  showAdminUserActions?: boolean;
}) {
  const {
    inModal,
    hideTabs,
    hideAddMemberButton,
    addMemberSignal,
    isAddMemberDialogOpen,
    onAddMemberDialogOpenChange,
    addMemberDraft,
    onAddMemberDraftChange,
    controlledActiveTab,
    onControlledTabChange,
    onAddMemberActionDisabledChange,
    facilityId: facilityIdProp,
    showAdminUserActions = false,
  } = props;
  const searchParams = useSearchParams();
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [showInfoPopover, setShowInfoPopover] = useState(true);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [jobTitleFilter, setJobTitleFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortByValue | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const {
    userId,
    admin,
    adminUserId,
    user: sessionUser,
    type,
    profile: { userId: workplaceId, onboardingSteps },
    profile: workplaceProfile,
  } = useSelector((state: SessionType) => state.session);
  // When rendered from an admin context (e.g. the facility drawer's "Show
  // Users" modal), the caller passes the facility being viewed. Otherwise
  // fall back to the session userId, which is the workplace context for a
  // workplace-user session.
  const effectiveFacilityId = facilityIdProp ?? userId;
  const [addMemberReadinessState, setAddMemberReadinessState] = useState<AddMemberReadinessState>(
    () => ({
      facilityId: effectiveFacilityId,
      isReady: false,
    })
  );
  const history = useHistory();
  const canEditNotifications =
    (admin || sessionUser?.access?.includes(FACILITY_ROLES.ADMIN)) ?? false;
  const [isMounted, setIsMounted] = useState(false);
  const [infoPopoverAddTeamMember, setInfoPopoverAddTeamMember] = useLocalStorage(
    `${INFO_POPOVER_ADD_TEAM_MEMBER}_${userId}_${sessionUser?._id}`,
    false
  );
  const {
    teamMemberState,
    fetchTeamMembers,
    isLoading: isAPILoading,
    getUsersMutation,
    linkUser,
    fetchUserWithEmail,
    updateUser,
    createUser,
    cancel,
    dispatch,
    handleAddTeamMemberOnboardingStep,
    hasLoadedTeamMembers,
    addTeamMembersOnboardingUpdateStatus,
  } = useTeamMembersApiHook({
    setIsDebouncing,
  });
  const isAddMemberDialogControlled = isDefined(isAddMemberDialogOpen);
  const processModalVisibility =
    isAddMemberDialogOpen === true ? PROCESS_MODAL_STATE.add : teamMemberState.processModal;

  const isLoading = isAPILoading || isDebouncing;
  const isUpdatingAddTeamMemberOnboardingStep = addTeamMembersOnboardingUpdateStatus === "pending";
  const sessionUserAccess = sessionUser?.access ?? [];
  const canCompleteAddTeamMemberOnboardingStep =
    isDefined(sessionUser?._id) &&
    (sessionUserAccess.includes(FACILITY_ROLES.ADMIN) ||
      sessionUserAccess.includes(FACILITY_ROLES.SHIFT_MANAGEMENT));
  const shouldCompleteAddTeamMemberOnboardingStep =
    !admin &&
    canCompleteAddTeamMemberOnboardingStep &&
    teamMemberState.teamMembers.length > 1 &&
    onboardingSteps?.addTeamMembers === false;
  const isAddMemberOnboardingStepSettling =
    !admin &&
    onboardingSteps?.addTeamMembers === false &&
    addTeamMembersOnboardingUpdateStatus !== "failed" &&
    (isAPILoading ||
      shouldCompleteAddTeamMemberOnboardingStep ||
      isUpdatingAddTeamMemberOnboardingStep ||
      addTeamMembersOnboardingUpdateStatus === "succeeded");
  const canMarkAddMemberActionReady = hasLoadedTeamMembers && !isAddMemberOnboardingStepSettling;
  const isAddMemberActionReady =
    addMemberReadinessState.facilityId === effectiveFacilityId && addMemberReadinessState.isReady;
  const isAddMemberActionDisabled = !isAddMemberActionReady;
  const fetchTeamMembersDebounce = useDebounce(fetchTeamMembers, []);

  const { data: supportArticlesData } = useSupportArticles();
  const supportArticleUrl = findSupportArticleUrlById(
    supportArticlesData,
    "user-roles-and-permissions"
  );
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!admin) {
      logEvent(HCF_USER_EVENTS.USERS_VIEWED);
    }
  }, [admin]);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: "SET_ACTIVE_TAB",
      activeTab: (searchParams.get("category") as TAB_STATE) ?? TAB_STATE.ACTIVE,
    });
    dispatch({
      type: "SET_FILTER",
      filter: searchParams.get("search") ?? "",
    });
    const isArchived = searchParams.get("category") === "inactive";
    const search = searchParams.get("search") ?? "";
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });

    void fetchTeamMembers({
      userId: effectiveFacilityId,
      isArchived,
      filter: search,
      resetData: true,
    });
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFacilityId, dispatch]);

  useEffect(() => {
    if (shouldCompleteAddTeamMemberOnboardingStep && isDefined(sessionUser?._id)) {
      void handleAddTeamMemberOnboardingStep(sessionUser._id, workplaceProfile);
    }
    // adding workplaceProfile to dependency array causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUser?._id, shouldCompleteAddTeamMemberOnboardingStep]);

  useEffect(() => {
    setAddMemberReadinessState((currentState) => {
      if (currentState.facilityId !== effectiveFacilityId) {
        return {
          facilityId: effectiveFacilityId,
          isReady: false,
        };
      }

      if (currentState.isReady || !canMarkAddMemberActionReady) {
        return currentState;
      }

      return {
        facilityId: effectiveFacilityId,
        isReady: true,
      };
    });
  }, [canMarkAddMemberActionReady, effectiveFacilityId]);

  useEffect(() => {
    onAddMemberActionDisabledChange?.(isAddMemberActionDisabled);
  }, [isAddMemberActionDisabled, onAddMemberActionDisabledChange]);

  useEffect(() => {
    return () => {
      onAddMemberActionDisabledChange?.(false);
    };
  }, [onAddMemberActionDisabledChange]);

  const dispatchTeamMemberAction = useCallback(
    (action: TeamMemberActions) => {
      if (action.type === "SET_PROCESS_MODAL_VISIBILITY") {
        if (action.processModal === PROCESS_MODAL_STATE.add) {
          onAddMemberDialogOpenChange?.(true);

          if (isAddMemberDialogControlled) {
            return;
          }
        }

        if (action.processModal === PROCESS_MODAL_STATE.off) {
          onAddMemberDialogOpenChange?.(false);
        }
      }

      dispatch(action);
    },
    [dispatch, isAddMemberDialogControlled, onAddMemberDialogOpenChange]
  );

  const openAddMemberDialog = useCallback(() => {
    if (isAddMemberActionDisabled) {
      return;
    }

    dispatchTeamMemberAction({
      type: "SET_PROCESS_MODAL_VISIBILITY",
      processModal: PROCESS_MODAL_STATE.add,
    });
  }, [dispatchTeamMemberAction, isAddMemberActionDisabled]);

  const loadMore = () => {
    setIsDebouncing(true);
    const newPage = teamMemberState.teamMembers.length ? teamMemberState.page + 1 : 1;
    dispatch({
      type: "SET_PAGE_NUMBER",
      page: newPage,
    });
    const isArchived = teamMemberState.activeTab === "inactive";
    fetchTeamMembersDebounce({
      userId: effectiveFacilityId,
      isArchived,
      page: newPage,
      filter: teamMemberState.filter,
    });
  };

  const [infiniteScrollRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: teamMemberState.page * 10 < teamMemberState.count,
    disabled: getUsersMutation.isError,
    onLoadMore: loadMore,
  });

  const handleTabChange = (tabKey: string) => {
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
    dispatch({ type: "SET_FILTER", filter: "" });
    dispatch({
      type: "SET_PAGE_NUMBER",
      page: 1,
    });
    dispatch({ type: "SET_ACTIVE_TAB", activeTab: tabKey as TAB_STATE });
    if (!hideTabs) {
      updateSearchParams({ category: tabKey }, history.location.pathname, history);
    }
    onControlledTabChange?.(tabKey as TAB_STATE);

    void fetchTeamMembers({
      userId: effectiveFacilityId,
      isArchived: tabKey === "inactive",
      filter: "",
      resetData: true,
    });
  };

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (controlledActiveTab && controlledActiveTab !== teamMemberState.activeTab) {
      handleTabChange(controlledActiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledActiveTab]);

  const lastHandledAddMemberSignalRef = useRef(0);
  useEffect(() => {
    if (typeof addMemberSignal === "number" && addMemberSignal > 0) {
      if (addMemberSignal === lastHandledAddMemberSignalRef.current) {
        return;
      }

      if (isAddMemberActionDisabled) {
        return;
      }

      lastHandledAddMemberSignalRef.current = addMemberSignal;
      openAddMemberDialog();
    }
  }, [addMemberSignal, isAddMemberActionDisabled, openAddMemberDialog]);

  const handleSearch = (e: React.FormEvent<HTMLInputElement>) => {
    const { value: filter } = e.currentTarget;
    dispatch({ type: "SET_FILTER", filter });
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
    setIsDebouncing(true);
    fetchTeamMembersDebounce({
      userId: effectiveFacilityId,
      isArchived: teamMemberState.activeTab === "inactive",
      filter,
      resetData: true,
    });
    updateSearchParams(
      { search: filter, category: teamMemberState.activeTab },
      history.location.pathname,
      history
    );
  };

  const handleRoleDetailsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.open(supportArticleUrl, "_blank");
    logEvent(
      HCF_USER_EVENTS.VISIT_ROLE_DETAILS,
      generateLogData(sessionUser?._id || "", admin, adminUserId, type, workplaceId)
    );
  };

  const handleCloseInfoPopover = () => {
    setShowInfoPopover(false);
    setInfoPopoverAddTeamMember(true);
    if (!admin) {
      logEvent(HCF_USER_EVENTS.GETTING_STARTED, {
        action: GETTING_STARTED.TEAM_ADDITION_INFO_POPOVER_CLOSED,
        workplaceId,
        workplaceUserId: sessionUser?._id,
        onboardingState: onboardingSteps,
      });
    }
  };

  const getTeamMembers = (id: string, isArchived: boolean) => {
    fetchTeamMembersDebounce({ userId: id, isArchived, resetData: true });
  };

  // Get unique job titles for filter
  const uniqueJobTitles = useMemo(() => {
    const titles = teamMemberState.teamMembers
      .map((member) => member.designation)
      .filter((designation): designation is string => !!designation);
    return Array.from(new Set(titles)).sort();
  }, [teamMemberState.teamMembers]);

  // Handle sort
  const handleSort = (column: "name" | "role" | "jobTitle") => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedTeamMembers = useMemo(() => {
    // Apply filters
    let filtered = teamMemberState.teamMembers;

    if (roleFilter.length > 0) {
      filtered = filtered.filter((member) =>
        roleFilter.some((role) => member.roles?.includes(role))
      );
    }

    if (jobTitleFilter.length > 0) {
      filtered = filtered.filter(
        (member) => member.designation && jobTitleFilter.includes(member.designation)
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let compareValue = 0;

        if (sortBy === "name") {
          compareValue = (a.name || "").localeCompare(b.name || "");
        } else if (sortBy === "role") {
          // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ CORP: { label: string; position:
          const aHighestRole = ROLE_MAPPINGS[getHighestAccessRole(a.roles, true)]?.position ?? -1;
          // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ CORP: { label: string; position:
          const bHighestRole = ROLE_MAPPINGS[getHighestAccessRole(b.roles, true)]?.position ?? -1;
          compareValue = bHighestRole - aHighestRole; // Higher position = more senior
        } else if (sortBy === "jobTitle") {
          compareValue = (a.designation || "").localeCompare(b.designation || "");
        }

        return sortOrder === "asc" ? compareValue : -compareValue;
      });
    }

    return filtered;
  }, [teamMemberState.teamMembers, roleFilter, jobTitleFilter, sortBy, sortOrder]);

  const emailCount = useMemo(
    () => filteredAndSortedTeamMembers.filter((member) => Boolean(member.email)).length,
    [filteredAndSortedTeamMembers]
  );

  const handleCopyAllEmails = () => {
    const emails = filteredAndSortedTeamMembers
      .map((member) => member.email)
      .filter((email) => Boolean(email));

    if (emails.length === 0) {
      return;
    }

    const emailsString = emails.join(", ");
    void navigator.clipboard.writeText(emailsString);
    showSuccessToast(`Copied ${emails.length} email(s) to clipboard`);
  };

  if (!isMounted) {
    return null;
  }
  return (
    <Wrapper
      {...{ [TEAM_MEMBERS_ADD_READY_ATTRIBUTE]: isAddMemberActionReady ? "true" : "false" }}
      data-testid={TEAM_MEMBERS_ADD_READINESS_TEST_ID}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {inModal ? <div /> : <PageHeader title="Team Members" />}
        {!hideAddMemberButton && (
          <InfoPopover
            variant="primary"
            content={<p>Here you can add a new team member and set their permissions</p>}
            open={showInfoPopover}
            placement="left"
            onHandleClose={handleCloseInfoPopover}
            showPopover={
              infoPopoverAddTeamMember === false &&
              onboardingSteps?.addTeamMembers === false &&
              !admin
            }
          >
            <RestrictedSignedProfile action="add new member" page="Team Members">
              <Button
                variant="primary"
                disabled={isAddMemberActionDisabled}
                onClick={openAddMemberDialog}
              >
                Add new member
              </Button>
            </RestrictedSignedProfile>
          </InfoPopover>
        )}
      </Box>
      {!hideTabs && (
        <WorkerTabs activeKey={teamMemberState.activeTab} onChange={handleTabChange}>
          <Tabs.TabPane tab="Active" key={"active"} />
          <Tabs.TabPane tab="Deactivated" key={"inactive"} />
        </WorkerTabs>
      )}
      <BulkActionsContainer
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <SearchField
          size="large"
          placeholder="Search team member"
          prefix={<SearchOutlined />}
          allowClear
          value={teamMemberState.filter}
          onChange={handleSearch}
          data-testid="team-member-search-input"
          style={{ flex: 1, minWidth: 240, margin: 0 }}
        />
        <Button variant="secondary" onClick={handleCopyAllEmails} disabled={emailCount === 0}>
          Copy All Emails ({emailCount})
        </Button>
      </BulkActionsContainer>
      {!isDesktop && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            marginBottom: 2,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
            <Autocomplete
              sx={{ flex: 1, minWidth: 0 }}
              size="small"
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sortBy) ?? null}
              onChange={(_, option) => {
                const nextValue = option?.value ?? null;
                setSortBy(nextValue);
                if (isDefined(nextValue) && nextValue !== sortBy) {
                  setSortOrder("asc");
                }
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} placeholder="Sort by" />}
            />
            {isDefined(sortBy) && (
              <IconButton
                size="small"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                aria-label={sortOrder === "asc" ? "Sort descending" : "Sort ascending"}
              >
                {sortOrder === "asc" ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              sx={{ flex: 1, minWidth: 0 }}
              size="small"
              options={ROLE_FILTER_OPTIONS}
              value={ROLE_FILTER_OPTIONS.filter((option) => roleFilter.includes(option.id))}
              onChange={(_, options) => setRoleFilter(options.map((option) => option.id))}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} placeholder="Filter by role" />}
            />
            {supportArticleUrl && (
              <IconButton
                component="a"
                href={supportArticleUrl}
                onClick={handleRoleDetailsClick}
                aria-label="Learn more about roles"
                size="small"
                sx={{ padding: 0 }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {uniqueJobTitles.length > 0 && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              sx={{ width: "100%" }}
              size="small"
              options={uniqueJobTitles}
              value={jobTitleFilter}
              onChange={(_, values) => setJobTitleFilter(values)}
              renderInput={(params) => <TextField {...params} placeholder="Filter by job title" />}
            />
          )}
        </Box>
      )}
      {isDesktop && (
        <WorkerRow header>
          <Column flex={1.25}>
            <SortableHeader onClick={() => handleSort("name")}>
              MEMBERS
              {sortBy === "name" &&
                (sortOrder === "asc" ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                ))}
            </SortableHeader>
          </Column>
          <Column>PHONE</Column>
          <Column>
            <ColumnHeaderWrapper>
              <SortableHeader onClick={() => handleSort("role")}>
                ROLES
                {sortBy === "role" &&
                  (sortOrder === "asc" ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ))}
              </SortableHeader>
              <Dropdown
                overlay={
                  <Menu>
                    <FilterDropdownContent>
                      <Select
                        mode="multiple"
                        style={{ minWidth: 200 }}
                        placeholder="Filter roles"
                        allowClear
                        value={roleFilter}
                        onChange={setRoleFilter}
                        maxTagCount="responsive"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {Object.entries(ROLE_MAPPINGS).map(([, value]) => (
                          <Select.Option key={value.id} value={value.id}>
                            {value.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </FilterDropdownContent>
                  </Menu>
                }
                trigger={["click"]}
              >
                <FilterIcon $isActive={roleFilter.length > 0}>
                  <FilterListIcon />
                </FilterIcon>
              </Dropdown>
              {supportArticleUrl && (
                <Tooltip
                  getPopupContainer={(triggerNode) => triggerNode}
                  title={
                    <a href={supportArticleUrl} onClick={handleRoleDetailsClick}>
                      Learn more about roles
                    </a>
                  }
                  placement="bottom"
                >
                  <InfoIcon />
                </Tooltip>
              )}
            </ColumnHeaderWrapper>
          </Column>
          <Column>
            <ColumnHeaderWrapper>
              <SortableHeader onClick={() => handleSort("jobTitle")}>
                JOB TITLE
                {sortBy === "jobTitle" &&
                  (sortOrder === "asc" ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ))}
              </SortableHeader>
              <Dropdown
                overlay={
                  <Menu>
                    <FilterDropdownContent>
                      <Select
                        mode="multiple"
                        style={{ minWidth: 200 }}
                        placeholder="Filter job titles"
                        allowClear
                        value={jobTitleFilter}
                        onChange={setJobTitleFilter}
                        maxTagCount="responsive"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {uniqueJobTitles.map((title) => (
                          <Select.Option key={title} value={title}>
                            {title}
                          </Select.Option>
                        ))}
                      </Select>
                    </FilterDropdownContent>
                  </Menu>
                }
                trigger={["click"]}
              >
                <FilterIcon $isActive={jobTitleFilter.length > 0}>
                  <FilterListIcon />
                </FilterIcon>
              </Dropdown>
            </ColumnHeaderWrapper>
          </Column>
          {admin && <Column>IS SUSPICIOUS</Column>}
          {admin && <Column>SYNC KNOCK</Column>}
          {showAdminUserActions && teamMemberState.activeTab === TAB_STATE.ACTIVE && (
            <Column>IMPERSONATION</Column>
          )}
          <Column flex={canEditNotifications ? 1 : 0.5}>
            QUICK ACTION{canEditNotifications && "S"}
          </Column>
        </WorkerRow>
      )}
      {processModalVisibility !== PROCESS_MODAL_STATE.off && (
        <EditTeamMemberDialog
          activeTab={teamMemberState.activeTab}
          dispatch={dispatchTeamMemberAction}
          existingMemberEmails={teamMemberState.teamMembers
            .map((member) => member.email)
            .filter((email) => isDefined(email))}
          member={teamMemberState.member}
          fetchUserWithEmail={fetchUserWithEmail}
          visibility={processModalVisibility}
          addMemberDraft={addMemberDraft}
          onAddMemberDraftChange={onAddMemberDraftChange}
          linkUser={linkUser}
          updateUser={updateUser}
          createUser={createUser}
          getTeamMembers={getTeamMembers}
          cancel={cancel}
          facilityId={effectiveFacilityId}
        />
      )}
      <TeamMemberStatusModal
        activeTab={teamMemberState.activeTab}
        dispatch={dispatch}
        member={teamMemberState.member}
        getTeamMembers={getTeamMembers}
        visibility={teamMemberState.statusModal}
      />
      <TeamMemberUnlinkModal
        dispatch={dispatch}
        member={teamMemberState.member}
        getTeamMembers={getTeamMembers}
        visibility={teamMemberState.unlinkModal}
      />
      <NotificationPreferencesDialogContainer
        member={teamMemberState.member}
        visibility={teamMemberState.notificationModal}
        dispatch={dispatch}
        onMemberSaved={() =>
          getTeamMembers(effectiveFacilityId, teamMemberState.activeTab === "inactive")
        }
      />
      {(() => {
        if (filteredAndSortedTeamMembers.length === 0) {
          return !isLoading && <EmptyPanel filter={teamMemberState.filter} />;
        }
        const rows = filteredAndSortedTeamMembers.map((member) => {
          return (
            <TeamMemberRow
              member={member}
              canEditNotifications={canEditNotifications}
              key={`member-${member._id}`}
              dispatch={dispatch}
            />
          );
        });
        if (showAdminUserActions && teamMemberState.activeTab === TAB_STATE.ACTIVE) {
          return <WorkplaceUserAdminActionsProvider>{rows}</WorkplaceUserAdminActionsProvider>;
        }
        return rows;
      })()}
      {(isLoading || teamMemberState.teamMembers.length < teamMemberState.count) && (
        <LoadingContainer ref={infiniteScrollRef}>
          <LoadingIcon indicator={<LoadingOutlined spin />} />
        </LoadingContainer>
      )}
    </Wrapper>
  );
}
